import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Table } from '@mantine/core'
import { cn } from '@/utils/cn'
import { useClient, type OrpcClient } from '@/hooks/useClient'
import { AdminPanel } from './AdminPanel'
import { UnderlineTabs } from './UnderlineTabs'
import { ProviderLinksCard } from './ProviderLinksCard'
import { SyncControlsCard } from './SyncControlsCard'
import { AppointmentsCard } from './AppointmentsCard'
import {
  ID_CHIP,
  PAGE,
  PAGE_TITLE,
  PRIMARY_BTN,
  SECONDARY_BTN,
  TABLE_HEAD,
  TABLE_ROW,
} from './tokens'

type ConfigResult = Awaited<ReturnType<OrpcClient['zocdocConfig']>>
type ConnectionResult = Awaited<ReturnType<OrpcClient['zocdocTestConnection']>>
type ReadProbeResult = Awaited<ReturnType<OrpcClient['zocdocPingRead']>>

/**
 * Integration dashboard — the ops surface for the Zocdoc ↔ beHuman integration
 * (docs/ZOCDOC_INTEGRATION_PLAN.md §4.9, tasks in docs/ZOCDOC_INTEGRATION_TASKS.md).
 * Rendered by `pages/Super/Integrations.tsx` at `/super/integrations`, so it is super-admin only —
 * the `/super` layout's own guard is what decides that, and this page adds none of its own.
 *
 * Three tabs, ordered by how often an operator needs them: **Appointments** (what Zocdoc holds
 * today, and where a booking is made), **Providers** (the links and their published availability),
 * **Sandbox** (configuration, probes, crosswalks and the sections later deliverables land in).
 * The Appointments card has a second home on Schedule, where care coordinators work — it is the
 * same component, so a change here lands in both places.
 *
 * The page wears the behuman-v2 language — see `tokens.ts` — so it reads as part of the console
 * rather than as a raw internal tool.
 */

const TABS = [
  { id: 'appointments', label: 'Appointments' },
  { id: 'providers', label: 'Providers' },
  { id: 'sandbox', label: 'Sandbox' },
] as const

type TabId = (typeof TABS)[number]['id']

// `unknown` is orange, not grey: an undeclared environment is a live hazard once availability
// publishing exists, not a cosmetic gap. The host cannot identify the environment on its own —
// api-developer.zocdoc.com serves whichever environment the credentials belong to.
const ENVIRONMENT_CHIP: Record<ConfigResult['environment'], string> = {
  sandbox: 'bg-[#DBEAFE] text-[#1D4ED8]',
  production: 'bg-[#FEE2E2] text-[#DC2626]',
  unknown: 'bg-[#FEF3C7] text-[#B45309]',
}

/** Chips are plain spans in Mulish uppercase, matching Schedule's controls rather than Mantine. */
function Chip({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span
      className={cn([
        'behuman-v2-mulish inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] uppercase',
        tone,
      ])}
    >
      {children}
    </span>
  )
}

/** Label/value rows. A definition list, not a table — these are facts about one thing. */
function FactRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-0.5 border-b border-[#ECEEF1] py-2 last:border-b-0">
      <dt className="w-[160px] shrink-0 text-[13px] font-bold text-[#6B7280]">{label}</dt>
      <dd className="m-0 min-w-0 text-[13px] break-all text-[#1F2733]">{value}</dd>
    </div>
  )
}

function ConfigCard({ config, error }: { config: ConfigResult | null; error: string | null }) {
  return (
    <AdminPanel
      title="Configuration"
      badge={
        config && <Chip tone={ENVIRONMENT_CHIP[config.environment]}>{config.environment}</Chip>
      }
      description="Which Zocdoc the credentials belong to, and which secrets are set. The config is the switch: unset values mean the integration is off."
    >
      <div className="flex flex-col gap-3">
        {error && (
          <Alert color="red" radius="md">
            {error}
          </Alert>
        )}

        {config?.environmentWarning && (
          <Alert color="orange" radius="md" title="Environment is not confirmed">
            {config.environmentWarning}
          </Alert>
        )}

        {config && (
          <dl className="m-0">
            <FactRow label="Auth URL" value={config.authUrl ?? '—'} />
            <FactRow label="Audience" value={config.audience ?? '—'} />
            <FactRow label="API base" value={config.baseUrl ?? '—'} />
            <FactRow
              label="Client id / secret"
              value={`${config.hasClientId ? 'set' : 'unset'} / ${
                config.hasClientSecret ? 'set' : 'unset'
              }`}
            />
          </dl>
        )}

        {config?.missingEnv.length ? (
          <Alert color="yellow" radius="md" title="Integration is off">
            Unset: {config.missingEnv.join(', ')}
          </Alert>
        ) : null}
      </div>
    </AdminPanel>
  )
}

function ProbesCard() {
  const client = useClient()
  const [tokenLoading, setTokenLoading] = useState(false)
  const [readLoading, setReadLoading] = useState(false)
  const [token, setToken] = useState<ConnectionResult | null>(null)
  const [read, setRead] = useState<ReadProbeResult | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const describe = (error: unknown) =>
    setFailure(error instanceof Error ? error.message : 'Request failed')

  const testToken = async () => {
    setTokenLoading(true)
    setToken(null)
    setFailure(null)
    try {
      setToken(await client.zocdocTestConnection())
    } catch (error) {
      describe(error)
    } finally {
      setTokenLoading(false)
    }
  }

  const testRead = async () => {
    setReadLoading(true)
    setRead(null)
    setFailure(null)
    try {
      setRead(await client.zocdocPingRead())
    } catch (error) {
      describe(error)
    } finally {
      setReadLoading(false)
    }
  }

  return (
    <AdminPanel
      title="Connectivity probes"
      description={
        <>
          The token probe proves the credentials; the read probe proves{' '}
          <span className={ID_CHIP}>ZOCDOC_BASE_URL</span> and the granted scopes. Neither returns a
          token.
        </>
      }
      actions={
        <>
          <Button {...PRIMARY_BTN} onClick={testToken} loading={tokenLoading}>
            Test credentials
          </Button>
          <Button {...SECONDARY_BTN} onClick={testRead} loading={readLoading}>
            Test authenticated read
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {failure && (
          <Alert color="red" radius="md" title="Could not reach middleware">
            {failure}
          </Alert>
        )}

        {token && (
          <Alert
            color={token.ok ? 'green' : 'red'}
            radius="md"
            title={token.ok ? 'Zocdoc issued a token' : 'Zocdoc did not issue a token'}
          >
            <dl className="m-0">
              {token.error && <FactRow label="Error" value={token.error} />}
              {token.missingEnv?.length ? (
                <FactRow label="Unset" value={token.missingEnv.join(', ')} />
              ) : null}
              {token.status !== undefined && <FactRow label="HTTP" value={token.status} />}
              {token.tokenType && <FactRow label="Token type" value={token.tokenType} />}
              {token.expiresInSeconds !== undefined && (
                <FactRow label="Expires in" value={`${token.expiresInSeconds}s`} />
              )}
              {token.scope && <FactRow label="Scopes" value={token.scope} />}
            </dl>
          </Alert>
        )}

        {read && (
          <Alert
            color={read.ok ? 'green' : 'red'}
            radius="md"
            title={read.ok ? 'Authenticated read succeeded' : 'Authenticated read failed'}
          >
            <dl className="m-0">
              {read.error && <FactRow label="Error" value={read.error} />}
              {read.missingEnv?.length ? (
                <FactRow label="Unset" value={read.missingEnv.join(', ')} />
              ) : null}
              {read.status !== undefined && <FactRow label="HTTP" value={read.status} />}
              {read.requestId && <FactRow label="Zocdoc request id" value={read.requestId} />}
              {read.totalCount !== undefined && (
                <FactRow label="Schedulable entities" value={read.totalCount} />
              )}
            </dl>

            {read.entities?.length ? (
              <Table verticalSpacing={6} fz="sm" mt="sm">
                <Table.Thead>
                  <Table.Tr className={TABLE_ROW}>
                    <Table.Th className={TABLE_HEAD}>Provider · location</Table.Th>
                    <Table.Th className={TABLE_HEAD}>Specialty</Table.Th>
                    <Table.Th className={TABLE_HEAD}>Time zone</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {read.entities.map((entity) => (
                    <Table.Tr key={entity.id} className={TABLE_ROW}>
                      <Table.Td>
                        <span className={ID_CHIP}>{entity.id}</span>
                      </Table.Td>
                      <Table.Td>{entity.mainSpecialtyName ?? '—'}</Table.Td>
                      <Table.Td>{entity.timeZone ?? '—'}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : null}
          </Alert>
        )}
      </div>
    </AdminPanel>
  )
}

export function IntegrationDashboardPage() {
  const client = useClient()
  const [config, setConfig] = useState<ConfigResult | null>(null)
  const [configError, setConfigError] = useState<string | null>(null)
  const [tab, setTab] = useState<TabId>('appointments')

  const loadConfig = useCallback(async () => {
    try {
      setConfig(await client.zocdocConfig())
    } catch (error) {
      setConfigError(error instanceof Error ? error.message : 'Could not load Zocdoc config')
    }
  }, [client])

  useEffect(() => {
    void loadConfig()
  }, [loadConfig])

  return (
    <div className={PAGE}>
      <div className="mx-auto flex max-w-[1400px] flex-col gap-4">
        <header>
          <h1 className={PAGE_TITLE}>Zocdoc dashboard</h1>

          <UnderlineTabs
            idPrefix="zocdoc-tab"
            panelId="zocdoc-tabpanel"
            label="Zocdoc dashboard sections"
            value={tab}
            onChange={setTab}
            tabs={TABS.map(({ id, label }) => ({ id, label }))}
            className="mt-3"
          />
        </header>

        <div
          role="tabpanel"
          id="zocdoc-tabpanel"
          aria-labelledby={`zocdoc-tab-${tab}`}
          className="flex flex-col gap-4"
        >
          {tab === 'appointments' && <AppointmentsCard />}

          {tab === 'providers' && (
            <>
              <SyncControlsCard />
              <ProviderLinksCard />
            </>
          )}

          {tab === 'sandbox' && (
            <>
              <ConfigCard config={config} error={configError} />
              <ProbesCard />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
